import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { EmbedToken, embedTokenApi } from '../../../services/storefrontService';
import MaterialSymbol from '../../common/MaterialSymbol';
import { buildEmbedSnippet, EmbedMode } from './embedSnippet';

export interface EmbedSnippetDialogProps {
    /** `null` = geschlossen. Ohne `token`-Feld wird das Token per `reveal` nachgeladen. */
    embed: EmbedToken | null;
    onClose: () => void;
}

/** Snippet (Copy-Button) und Vorschau eines Widgets. */
const EmbedSnippetDialog: React.FC<EmbedSnippetDialogProps> = ({ embed, onClose }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const tr = (key: string) => translate(`embed.snippet.${key}`);
    const [token, setToken] = useState<string | null>(null);
    const [mode, setMode] = useState<EmbedMode>('script');
    const [preview, setPreview] = useState<{ html: string } | 'error' | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        setToken(embed?.token ?? null);
        setPreview(null);
        setFailed(false);
        if (!embed) return;
        let cancelled = false;
        if (!embed.token) {
            embedTokenApi.reveal(embed.id).then((t) => { if (!cancelled) setToken(t.token ?? null); }).catch(() => { if (!cancelled) setFailed(true); });
        }
        embedTokenApi.preview(embed.id, embed.config.theme ?? 'auto', 'de')
            .then((html) => { if (!cancelled) setPreview({ html }); })
            .catch(() => { if (!cancelled) setPreview('error'); });
        return () => { cancelled = true; };
    }, [embed]);

    const theme = embed?.config.theme ?? 'auto';
    const code = token && embed ? buildEmbedSnippet(token, theme, mode) : '';

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            notify(tr('copied'), { type: 'success' });
        } catch {
            notify(tr('copyFailed'), { type: 'warning' });
        }
    };

    return (
        <Dialog open={embed !== null} onClose={onClose} fullWidth maxWidth="md" aria-labelledby="embed-snippet-title">
            <DialogTitle id="embed-snippet-title">{tr('title')}{embed ? `: ${embed.label}` : ''}</DialogTitle>
            <DialogContent>
                <Typography color="text.secondary" sx={{ mb: 2 }}>{tr('intro')}</Typography>
                {failed ? <Alert severity="error" sx={{ mb: 2 }}>{tr('revealError')}</Alert> : null}
                <TextField select size="small" label={tr('mode')} value={mode} onChange={(e) => setMode(e.target.value as EmbedMode)} sx={{ mb: 2, minWidth: { xs: '100%', sm: 320 } }}>
                    <MenuItem value="script">{tr('modeScript')}</MenuItem>
                    <MenuItem value="iframe">{tr('modeIframe')}</MenuItem>
                </TextField>

                <Typography variant="subtitle2" component="h3" sx={{ mb: 0.5 }}>{tr('code')}</Typography>
                <Box
                    component="pre"
                    data-testid="embed-snippet-code"
                    sx={{ m: 0, mb: 1, p: 1.5, overflowX: 'auto', border: '1px solid var(--oa-divider)', borderRadius: 1, bgcolor: 'var(--oa-surface-subtle, transparent)', fontFamily: 'monospace', fontSize: 13, fontStyle: 'normal', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
                >
                    {code || <CircularProgress size={16} />}
                </Box>
                <Button size="small" variant="outlined" startIcon={<MaterialSymbol name="content_copy" />} onClick={copy} disabled={!code}>
                    {tr('copy')}
                </Button>

                <Typography variant="subtitle2" component="h3" sx={{ mt: 3, mb: 0.5 }}>{tr('preview')}</Typography>
                {preview === null ? <CircularProgress size={20} /> : null}
                {preview === 'error' ? <Alert severity="warning">{tr('previewError')}</Alert> : null}
                {preview && preview !== 'error' ? (
                    <Box
                        component="iframe"
                        title={tr('previewTitle')}
                        sandbox=""
                        srcDoc={`<!doctype html><meta charset="utf-8"><body style="margin:0">${preview.html}`}
                        sx={{ width: '100%', minHeight: 260, border: '1px solid var(--oa-divider)', borderRadius: 1, display: 'block' }}
                    />
                ) : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{tr('close')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default EmbedSnippetDialog;
